'use client'

import Link from 'next/link'
import { memo } from 'react'

import { Button, CardSimple } from '@fp/ui-kit'
import { useReportError, type ReportableError } from '@hooks/useReportError'

type ErrorFallbackProps = {
  error: ReportableError
  reset: () => void
  /** Distinguishes which boundary caught the error in Datadog RUM. */
  source: string
  title?: string
}

export const ErrorFallback = memo(({ error, reset, source, title = 'Something went wrong' }: ErrorFallbackProps) => {
  useReportError(error, source)

  return (
    <CardSimple className='min-w-[300px] max-w-[90vw] sm:min-w-lg sm:max-w-xl'>
      <div className='text-center p-2 sm:p-4 w-full'>
        <h2 className='text-2xl font-semibold mb-4'>{title}</h2>
        <div className='overflow-y-auto max-h-[400px] whitespace-break-spaces break-words'>
          {error?.message && (
            <p className='mb-4 mt-2 text-destructive-foreground bg-destructive p-2 rounded-lg'>{`Error: "${error.message}"`}</p>
          )}
          {error?.digest && (
            <p className='mb-4 text-base font-semibold text-muted-foreground'>Digest: {error.digest}</p>
          )}
        </div>

        <div className='flex flex-col gap-2 mt-6 justify-center'>
          <Button onClick={() => reset()} variant='secondary'>
            Try again
          </Button>

          <Button onClick={() => window.location.reload()} variant='primary'>
            Reload page
          </Button>

          <Link href='/'>
            <Button variant='outline' className='w-full'>
              Home page
            </Button>
          </Link>
        </div>
      </div>
    </CardSimple>
  )
})

ErrorFallback.displayName = 'ErrorFallback'
